import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Paper, 
  IconButton,
  Tooltip,
  Chip,
  List,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Skeleton,
  Alert,
  AlertTitle
} from '@mui/material';
import { 
  Inventory as InventoryIcon, 
  ShoppingCart as ShoppingCartIcon, 
  TrendingUp as TrendingUpIcon,
  Warehouse as WarehouseIcon,
  Receipt as ReceiptIcon,
  Warning as WarningIcon,
  AccessTime as AccessTimeIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  LocalShipping as SupplierIcon,
  Category as CategoryIcon,
  Store as BranchIcon,
  AccountTree as ChartIcon,
  PlaylistAddCheck as StockCountIcon,
  Notifications as NotificationsIcon,
  Speed as SpeedIcon,
  PointOfSale as PointOfSaleIcon,
  AccountBalanceWallet as CustodyIcon,
  PostAdd as DraftIcon
} from '@mui/icons-material';
import { 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  ResponsiveContainer,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend
} from 'recharts';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useAppSelector } from '../../hooks/redux.hooks';
import dashboardService, { 
  DashboardStats, 
  PurchasesChartData, 
  SalesChartData,
  BranchSales,
  InventoryDistribution, 
  RecentActivity,
  DashboardAlert
} from '../../services/dashboardService';

const translations = {
  en: {
    welcome: 'Welcome back',
    subtitle: 'Here\'s what\'s happening with your business today',
    // Stats
    totalItems: 'Total Items',
    totalCategories: 'Categories',
    totalWarehouses: 'Warehouses',
    totalBranches: 'Branches',
    totalSuppliers: 'Suppliers',
    totalCustomers: 'Customers',
    totalPurchases: 'Purchases',
    totalAccounts: 'Accounts',
    pendingOrders: 'Pending Orders',
    lowStock: 'Low Stock Items',
    // NEW Stats
    todaySales: 'Today Sales',
    monthSales: 'Month Sales',
    yesterdaySales: 'Yesterday Sales',
    lastMonthSales: 'Last Month Sales',
    openCustodies: 'Open Custodies',
    custodiesBalance: 'Custodies Balance',
    draftEntries: 'Draft Entries',
    // Charts
    purchasesOverview: 'Purchases Overview',
    salesOverview: 'Sales Overview',
    last7Days: 'Last 7 Days',
    last30Days: 'Last 30 Days',
    branchComparison: 'Branch Sales Comparison',
    inventoryDistribution: 'Inventory Distribution',
    topCategories: 'Top Categories',
    purchases: 'Purchases',
    returns: 'Returns',
    sales: 'Sales',
    totalSalesAmount: 'Total Sales',
    salesCount: 'Count',
    // Alerts
    alerts: 'Alerts & Notifications',
    lowStockAlert: 'Low stock alert',
    itemsNeedReorder: 'items need reorder',
    pendingApprovals: 'Pending approvals',
    purchasesPending: 'purchases waiting for approval',
    expiringItems: 'Expiring soon',
    itemsExpiring: 'items expiring in 30 days',
    noAlerts: 'No alerts at this time',
    // Quick Actions
    quickActions: 'Quick Actions',
    newPurchase: 'New Purchase',
    addItem: 'Add Item',
    stockCount: 'Stock Count',
    viewReports: 'View Reports',
    // Recent Activity
    recentActivity: 'Recent Activity',
    noActivity: 'No recent activity',
    // Performance
    performance: 'System Performance',
    activeUsers: 'Active Users',
    todayTransactions: 'Today\'s Transactions',
    systemHealth: 'System Health',
    healthy: 'Healthy',
    // Time
    today: 'Today',
    thisWeek: 'This Week',
    thisMonth: 'This Month',
    // Days
    sun: 'Sun',
    mon: 'Mon',
    tue: 'Tue',
    wed: 'Wed',
    thu: 'Thu',
    fri: 'Fri',
    sat: 'Sat',
    // Currency
    currency: 'KWD',
    // Misc
    viewAll: 'View All',
    refresh: 'Refresh',
    loading: 'Loading...',
  },
  ar: {
    welcome: 'مرحباً بعودتك',
    subtitle: 'إليك ما يحدث في نشاطك التجاري اليوم',
    // Stats
    totalItems: 'إجمالي الأصناف',
    totalCategories: 'التصنيفات',
    totalWarehouses: 'المخازن',
    totalBranches: 'الفروع',
    totalSuppliers: 'الموردين',
    totalCustomers: 'العملاء',
    totalPurchases: 'المشتريات',
    totalAccounts: 'الحسابات',
    pendingOrders: 'طلبات معلقة',
    lowStock: 'مخزون منخفض',
    // NEW Stats
    todaySales: 'مبيعات اليوم',
    monthSales: 'مبيعات الشهر',
    yesterdaySales: 'مبيعات الأمس',
    lastMonthSales: 'مبيعات الشهر السابق',
    openCustodies: 'عهد مفتوحة',
    custodiesBalance: 'رصيد العهد',
    draftEntries: 'قيود مسودة',
    // Charts
    purchasesOverview: 'نظرة عامة على المشتريات',
    salesOverview: 'نظرة عامة على المبيعات',
    last7Days: 'آخر 7 أيام',
    last30Days: 'آخر 30 يوم',
    branchComparison: 'مقارنة مبيعات الفروع',
    inventoryDistribution: 'توزيع المخزون',
    topCategories: 'أهم التصنيفات',
    purchases: 'المشتريات',
    returns: 'المرتجعات',
    sales: 'المبيعات',
    totalSalesAmount: 'إجمالي المبيعات',
    salesCount: 'العدد',
    // Alerts
    alerts: 'التنبيهات والإشعارات',
    lowStockAlert: 'تنبيه مخزون منخفض',
    itemsNeedReorder: 'أصناف تحتاج إعادة طلب',
    pendingApprovals: 'موافقات معلقة',
    purchasesPending: 'مشتريات بانتظار الموافقة',
    expiringItems: 'قريبة الانتهاء',
    itemsExpiring: 'أصناف تنتهي صلاحيتها خلال 30 يوم',
    noAlerts: 'لا توجد تنبيهات حالياً',
    // Quick Actions
    quickActions: 'إجراءات سريعة',
    newPurchase: 'شراء جديد',
    addItem: 'إضافة صنف',
    stockCount: 'جرد المخزون',
    viewReports: 'عرض التقارير',
    // Recent Activity
    recentActivity: 'النشاط الأخير',
    noActivity: 'لا يوجد نشاط حديث',
    // Performance
    performance: 'أداء النظام',
    activeUsers: 'المستخدمون النشطون',
    todayTransactions: 'معاملات اليوم',
    systemHealth: 'صحة النظام',
    healthy: 'ممتاز',
    // Time
    today: 'اليوم',
    thisWeek: 'هذا الأسبوع',
    thisMonth: 'هذا الشهر',
    // Days
    sun: 'أحد',
    mon: 'إثن',
    tue: 'ثلا',
    wed: 'أرب',
    thu: 'خمي',
    fri: 'جمع',
    sat: 'سبت',
    // Currency
    currency: 'د.ك',
    // Misc
    viewAll: 'عرض الكل',
    refresh: 'تحديث',
    loading: 'جاري التحميل...',
  },
};

// ألوان البطاقات
const CARD_COLORS = {
  items: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  categories: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  warehouses: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  branches: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
  suppliers: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
  customers: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
  purchases: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
  accounts: 'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
  sales: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
  salesMonth: 'linear-gradient(135deg, #0ba360 0%, #3cba92 100%)',
  salesYesterday: 'linear-gradient(135deg, #00b09b 0%, #96c93d 100%)',
  salesLastMonth: 'linear-gradient(135deg, #134e5e 0%, #71b280 100%)',
  custodies: 'linear-gradient(135deg, #f7971e 0%, #ffd200 100%)',
  draft: 'linear-gradient(135deg, #ee9ca7 0%, #ffdde1 100%)',
  lowStock: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a5a 100%)',
  pending: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
};

const PIE_COLORS = ['#4facfe', '#667eea', '#f093fb', '#43e97b', '#fa709a'];
const BRANCH_COLORS = ['#4facfe', '#11998e', '#667eea', '#f093fb', '#fa709a', '#f7971e', '#43e97b', '#ee9ca7'];

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { mode } = useTheme();
  const t = translations[language];
  const isRTL = language === 'ar';
  
  const user = useAppSelector((state) => state.auth.user);
  const permissions = useAppSelector((state) => state.auth.permissions);
  
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats>({
    totalItems: 0,
    totalCategories: 0,
    totalWarehouses: 0,
    totalBranches: 0,
    totalSuppliers: 0,
    totalCustomers: 0,
    totalPurchaseInvoices: 0,
    totalAccounts: 0,
    pendingPurchaseOrders: 0,
    lowStockItems: 0,
    todayPurchasesTotal: 0,
    thisMonthPurchasesTotal: 0,
    todayDailySalesTotal: 0,
    thisMonthDailySalesTotal: 0,
    yesterdayDailySalesTotal: 0,
    lastMonthDailySalesTotal: 0,
    openCustodies: 0,
    custodiesBalance: 0,
    draftJournalEntries: 0,
  });
  const [purchasesChartData, setPurchasesChartData] = useState<PurchasesChartData[]>([]);
  const [salesChartData, setSalesChartData] = useState<SalesChartData[]>([]);
  const [branchSales, setBranchSales] = useState<BranchSales[]>([]);
  const [inventoryData, setInventoryData] = useState<InventoryDistribution[]>([]);
  const [recentActivities, setRecentActivities] = useState<RecentActivity[]>([]);
  const [alerts, setAlerts] = useState<DashboardAlert[]>([]);

  // ═══════════════════════════════════════════════════════════════
  // التحقق من الصلاحيات - يجب أن يملك المستخدم إحدى الصلاحيات
  // ═══════════════════════════════════════════════════════════════
  const hasPermission = (perms: string[]) => {
    if (user?.isSuperAdmin) return true;
    return perms.some(p => permissions.includes(p));
  };

  // التحقق من أن المستخدم يملك كل الصلاحيات المطلوبة
  const hasAllPermissions = (perms: string[]) => {
    if (user?.isSuperAdmin) return true;
    return perms.every(p => permissions.includes(p));
  };

  // ═══════════════════════════════════════════════════════════════
  // ترجمة أيام الأسبوع
  // ═══════════════════════════════════════════════════════════════
  const getDayName = (dayName: string) => {
    const days: { [key: string]: { en: string; ar: string } } = {
      'Sunday': { en: 'Sun', ar: 'أحد' },
      'Monday': { en: 'Mon', ar: 'إثن' },
      'Tuesday': { en: 'Tue', ar: 'ثلا' },
      'Wednesday': { en: 'Wed', ar: 'أرب' },
      'Thursday': { en: 'Thu', ar: 'خمي' },
      'Friday': { en: 'Fri', ar: 'جمع' },
      'Saturday': { en: 'Sat', ar: 'سبت' },
    };
    return days[dayName]?.[language] || dayName;
  };

  // ═══════════════════════════════════════════════════════════════
  // تنسيق العملة
  // ═══════════════════════════════════════════════════════════════
  const formatCurrency = (amount: number) => {
    return `${amount.toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 })} ${t.currency}`;
  };

  // ═══════════════════════════════════════════════════════════════
  // جلب البيانات من API - مقيد بالصلاحيات
  // كل استدعاء API محمي بفحص الصلاحية قبل الإرسال
  // ═══════════════════════════════════════════════════════════════
  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // الإحصائيات الرئيسية - الكل يراها لكن الباكند يفلتر حسب الدور
      const statsData = await dashboardService.getStats();
      setStats(statsData);

      // رسم المشتريات - فقط من لديه صلاحية عرض فواتير الشراء
      if (hasPermission(['PURCHASE_INVOICE_VIEW'])) {
        const chartData = await dashboardService.getPurchasesChart(7);
        setPurchasesChartData(chartData);
      }

      // رسم المبيعات + مقارنة الفروع - فقط من لديه صلاحية عرض المبيعات اليومية
      if (hasPermission(['DAILY_SALES_VIEW'])) {
        const salesData = await dashboardService.getSalesChart(7);
        setSalesChartData(salesData);
        const branchData = await dashboardService.getBranchSalesComparison(30);
        setBranchSales(branchData);
      }

      // توزيع المخزون - فقط من لديه صلاحية عرض المخزون
      if (hasPermission(['WAREHOUSE_VIEW', 'STOCK_LEVEL_VIEW'])) {
        const distribution = await dashboardService.getInventoryDistribution();
        setInventoryData(distribution);
      }

      // النشاط الأخير - الكل يراه لكن الباكند يفلتر حسب الدور
      const activitiesData = await dashboardService.getRecentActivities(10);
      setRecentActivities(activitiesData);

      // التنبيهات - الكل يراها لكن الباكند يفلتر حسب الدور
      const alertsData = await dashboardService.getAlerts();
      setAlerts(alertsData);

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // ═══════════════════════════════════════════════════════════════
  // أيقونة النشاط حسب النوع
  // ═══════════════════════════════════════════════════════════════
  const getActivityIcon = (iconName: string) => {
    const iconMap: { [key: string]: React.ReactNode } = {
      'receipt': <ReceiptIcon fontSize="small" />,
      'point_of_sale': <PointOfSaleIcon fontSize="small" />,
      'inventory': <WarehouseIcon fontSize="small" />,
      'inventory_2': <InventoryIcon fontSize="small" />,
    };
    return iconMap[iconName] || <ReceiptIcon fontSize="small" />;
  };

  // لون النشاط حسب النوع
  const getActivityColor = (type: string) => {
    const colorMap: { [key: string]: string } = {
      'Purchase': '#4facfe',
      'DailySale': '#11998e',
      'StockIssue': '#f093fb',
      'Item': '#667eea',
    };
    return colorMap[type] || '#4facfe';
  };

  // ═══════════════════════════════════════════════════════════════
  // بطاقة الإحصائيات
  // ═══════════════════════════════════════════════════════════════
  const StatCard = ({ title, value, icon, gradient, permission, onClick }: {
    title: string;
    value: number | string;
    icon: React.ReactNode;
    gradient: string;
    permission?: string[];
    onClick?: () => void;
  }) => {
    if (permission && !hasPermission(permission)) return null;
    
    return (
      <Card 
        elevation={0}
        onClick={onClick}
        sx={{ 
          background: gradient,
          color: 'white',
          borderRadius: 3,
          cursor: onClick ? 'pointer' : 'default',
          transition: 'all 0.3s ease',
          position: 'relative',
          overflow: 'hidden',
          '&:hover': {
            transform: onClick ? 'translateY(-4px)' : 'none',
            boxShadow: onClick ? '0 12px 40px rgba(0,0,0,0.15)' : 'none',
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            right: isRTL ? 'auto' : 0,
            left: isRTL ? 0 : 'auto',
            width: '100px',
            height: '100px',
            background: 'rgba(255,255,255,0.1)',
            borderRadius: '50%',
            transform: 'translate(30%, -30%)',
          }
        }}
      >
        <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5, fontSize: '0.8rem' }}>
                {title}
              </Typography>
              {loading ? (
                <Skeleton variant="text" width={60} sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} />
              ) : (
                <Typography variant="h4" fontWeight="bold" sx={{ fontSize: '1.75rem' }}>
                  {typeof value === 'number' ? value.toLocaleString() : value}
                </Typography>
              )}
            </Box>
            <Box sx={{ 
              p: 1.5, 
              borderRadius: 2, 
              bgcolor: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {icon}
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  // ═══════════════════════════════════════════════════════════════
  // بطاقة الإجراء السريع
  // ═══════════════════════════════════════════════════════════════
  const QuickActionButton = ({ title, icon, onClick, permission }: {
    title: string;
    icon: React.ReactNode;
    onClick: () => void;
    permission?: string[];
  }) => {
    if (permission && !hasPermission(permission)) return null;
    
    return (
      <ListItemButton 
        onClick={onClick}
        sx={{ 
          borderRadius: 2, 
          mb: 1,
          bgcolor: mode === 'dark' ? 'rgba(79,172,254,0.08)' : 'rgba(79,172,254,0.05)',
          '&:hover': {
            bgcolor: mode === 'dark' ? 'rgba(79,172,254,0.15)' : 'rgba(79,172,254,0.1)',
          }
        }}
      >
        <ListItemIcon sx={{ color: mode === 'dark' ? '#4facfe' : '#0284c7', minWidth: 40 }}>
          {icon}
        </ListItemIcon>
        <ListItemText 
          primary={title} 
          primaryTypographyProps={{ 
            fontSize: '0.875rem',
            fontWeight: 500,
            color: mode === 'dark' ? '#e2e8f0' : '#334155'
          }} 
        />
        {isRTL ? <ArrowBackIcon fontSize="small" sx={{ color: '#94a3b8' }} /> : <ArrowForwardIcon fontSize="small" sx={{ color: '#94a3b8' }} />}
      </ListItemButton>
    );
  };

  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════
  return (
    <Box sx={{ direction: isRTL ? 'rtl' : 'ltr' }}>
      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* 👋 WELCOME SECTION */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <Box sx={{ 
        mb: 4, 
        display: 'flex', 
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', md: 'center' },
        gap: 2
      }}>
        <Box>
          <Typography 
            variant="h4" 
            fontWeight="bold" 
            sx={{ 
              fontSize: { xs: '1.5rem', md: '2rem' },
              color: mode === 'dark' ? '#f1f5f9' : '#0f172a',
              mb: 0.5
            }}
          >
            {t.welcome}{isRTL ? '،' : ','} {user?.fullName || user?.userName} 👋
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              color: mode === 'dark' ? '#94a3b8' : '#64748b',
              fontSize: { xs: '0.875rem', md: '1rem' }
            }}
          >
            {t.subtitle}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Chip 
            label={user?.roles?.[0] || 'User'} 
            size="small"
            sx={{ 
              bgcolor: mode === 'dark' ? 'rgba(79,172,254,0.2)' : 'rgba(2,132,199,0.1)',
              color: mode === 'dark' ? '#4facfe' : '#0284c7',
              fontWeight: 600,
              fontSize: '0.75rem'
            }}
          />
          <Tooltip title={t.refresh}>
            <IconButton 
              size="small" 
              onClick={() => fetchDashboardData()}
              sx={{ 
                bgcolor: mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                '&:hover': { bgcolor: mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* 📊 ROW 1: MAIN STATS                                         */}
      {/* أصناف + تصنيفات + مخازن + فروع                               */}
      {/* كل بطاقة مقيدة بصلاحية محددة                                  */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { 
          xs: 'repeat(2, 1fr)', 
          sm: 'repeat(2, 1fr)', 
          md: 'repeat(4, 1fr)' 
        },
        gap: 2,
        mb: 3
      }}>
        {/* الأصناف - يراها من لديه ITEMS_VIEW */}
        <StatCard 
          title={t.totalItems} 
          value={stats.totalItems} 
          icon={<InventoryIcon />} 
          gradient={CARD_COLORS.items}
          permission={['ITEMS_VIEW']}
          onClick={() => navigate('/items')}
        />
        {/* التصنيفات - يراها من لديه CATEGORY_VIEW */}
        <StatCard 
          title={t.totalCategories} 
          value={stats.totalCategories} 
          icon={<CategoryIcon />} 
          gradient={CARD_COLORS.categories}
          permission={['CATEGORY_VIEW']}
          onClick={() => navigate('/categories')}
        />
        {/* المخازن - يراها من لديه WAREHOUSE_VIEW (أمين المخزن يرى مخازنه فقط - الباكند يفلتر) */}
        <StatCard 
          title={t.totalWarehouses} 
          value={stats.totalWarehouses} 
          icon={<WarehouseIcon />} 
          gradient={CARD_COLORS.warehouses}
          permission={['WAREHOUSE_VIEW']}
          onClick={() => navigate('/warehouses')}
        />
        {/* الفروع - يراها من لديه BRANCH_VIEW (مدير الفرع يرى فروعه فقط - الباكند يفلتر) */}
        <StatCard 
          title={t.totalBranches} 
          value={stats.totalBranches} 
          icon={<BranchIcon />} 
          gradient={CARD_COLORS.branches}
          permission={['BRANCH_VIEW']}
          onClick={() => navigate('/branches')}
        />
      </Box>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* 💰 ROW 2: FINANCIAL STATS                                     */}
      {/* مبيعات اليوم + مبيعات الشهر + المشتريات + الحسابات           */}
      {/* المبيعات: فقط DAILY_SALES_VIEW (مدير الفرع يرى فرعه فقط)     */}
      {/* المشتريات: فقط PURCHASE_INVOICE_VIEW                          */}
      {/* الحسابات: فقط CHART_OF_ACCOUNTS_VIEW (محاسب/سوبر أدمن)       */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { 
          xs: 'repeat(2, 1fr)', 
          sm: 'repeat(2, 1fr)', 
          md: 'repeat(4, 1fr)' 
        },
        gap: 2,
        mb: 3
      }}>
        {/* مبيعات اليوم - فقط من لديه DAILY_SALES_VIEW */}
        <StatCard 
          title={t.todaySales} 
          value={formatCurrency(stats.todayDailySalesTotal)} 
          icon={<PointOfSaleIcon />} 
          gradient={CARD_COLORS.sales}
          permission={['DAILY_SALES_VIEW']}
          onClick={() => navigate('/daily-sales')}
        />
        {/* مبيعات الشهر - فقط من لديه DAILY_SALES_VIEW */}
        <StatCard
          title={t.monthSales}
          value={formatCurrency(stats.thisMonthDailySalesTotal)}
          icon={<TrendingUpIcon />}
          gradient={CARD_COLORS.salesMonth}
          permission={['DAILY_SALES_VIEW']}
        />
        {/* مبيعات الأمس — لأن مبيعات اليوم تُدخل صباح اليوم التالي 4ص، الأمس هو آخر يوم بيانات مكتملة */}
        <StatCard
          title={t.yesterdaySales}
          value={formatCurrency(stats.yesterdayDailySalesTotal)}
          icon={<PointOfSaleIcon />}
          gradient={CARD_COLORS.salesYesterday}
          permission={['DAILY_SALES_VIEW']}
          onClick={() => navigate('/daily-sales')}
        />
        {/* مبيعات الشهر السابق - فقط من لديه DAILY_SALES_VIEW */}
        <StatCard
          title={t.lastMonthSales}
          value={formatCurrency(stats.lastMonthDailySalesTotal)}
          icon={<TrendingUpIcon />}
          gradient={CARD_COLORS.salesLastMonth}
          permission={['DAILY_SALES_VIEW']}
        />
        {/* المشتريات - فقط من لديه PURCHASE_INVOICE_VIEW */}
        <StatCard 
          title={t.totalPurchases} 
          value={stats.totalPurchaseInvoices} 
          icon={<ShoppingCartIcon />} 
          gradient={CARD_COLORS.purchases}
          permission={['PURCHASE_INVOICE_VIEW']}
          onClick={() => navigate('/purchase-invoices')}
        />
        {/* الحسابات - فقط المحاسب أو السوبر أدمن */}
        <StatCard 
          title={t.totalAccounts} 
          value={stats.totalAccounts} 
          icon={<ChartIcon />} 
          gradient={CARD_COLORS.accounts}
          permission={['CHART_OF_ACCOUNTS_VIEW']}
          onClick={() => navigate('/chart-of-accounts')}
        />
      </Box>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* ⚠️ ROW 3: ALERT STATS                                        */}
      {/* مخزون منخفض + طلبات معلقة + عهد مفتوحة + قيود مسودة          */}
      {/* المخزون المنخفض: أمين المخزن STOCK_LEVEL_VIEW                 */}
      {/* الطلبات المعلقة: المشتري PURCHASE_ORDER_VIEW                  */}
      {/* العهد المفتوحة: مدير الفرع CUSTODY_VIEW                       */}
      {/* القيود المسودة: المحاسب JOURNAL_ENTRY_VIEW                    */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { 
          xs: 'repeat(2, 1fr)', 
          sm: 'repeat(2, 1fr)', 
          md: 'repeat(4, 1fr)' 
        },
        gap: 2,
        mb: 3
      }}>
        {/* المخزون المنخفض - أمين المخزن يرى مخازنه فقط */}
        <StatCard 
          title={t.lowStock} 
          value={stats.lowStockItems} 
          icon={<WarningIcon />} 
          gradient={CARD_COLORS.lowStock}
          permission={['STOCK_LEVEL_VIEW']}
        />
        {/* الطلبات المعلقة - فقط من لديه صلاحية أوامر الشراء */}
        <StatCard 
          title={t.pendingOrders} 
          value={stats.pendingPurchaseOrders} 
          icon={<AccessTimeIcon />} 
          gradient={CARD_COLORS.pending}
          permission={['PURCHASE_ORDER_VIEW']}
        />
        {/* العهد المفتوحة - فقط من لديه CUSTODY_VIEW */}
        <StatCard 
          title={t.openCustodies} 
          value={stats.openCustodies} 
          icon={<CustodyIcon />} 
          gradient={CARD_COLORS.custodies}
          permission={['CUSTODY_VIEW']}
          onClick={() => navigate('/custodies')}
        />
        {/* القيود المسودة - فقط المحاسب */}
        <StatCard 
          title={t.draftEntries} 
          value={stats.draftJournalEntries} 
          icon={<DraftIcon />} 
          gradient={CARD_COLORS.draft}
          permission={['JOURNAL_ENTRY_VIEW']}
          onClick={() => navigate('/journal-entries')}
        />
      </Box>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* 📈 PURCHASES CHART + SALES CHART                              */}
      {/* رسم المشتريات: فقط PURCHASE_INVOICE_VIEW                     */}
      {/* رسم المبيعات: فقط DAILY_SALES_VIEW                           */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
        gap: 3,
        mb: 3
      }}>
        {/* رسم بياني للمشتريات - فقط من لديه صلاحية فواتير الشراء */}
        {hasPermission(['PURCHASE_INVOICE_VIEW']) && (
          <Paper 
            elevation={0}
            sx={{ 
              p: 3,
              borderRadius: 3,
              bgcolor: mode === 'dark' ? '#1e293b' : '#ffffff',
              border: `1px solid ${mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box>
                <Typography variant="h6" fontWeight="bold" sx={{ color: mode === 'dark' ? '#f1f5f9' : '#0f172a' }}>
                  {t.purchasesOverview}
                </Typography>
                <Typography variant="body2" component="div" sx={{ color: mode === 'dark' ? '#94a3b8' : '#64748b' }}>
                  {t.last7Days}
                </Typography>
              </Box>
              <Chip 
                label={t.thisWeek}
                size="small"
                sx={{ 
                  bgcolor: mode === 'dark' ? 'rgba(79,172,254,0.15)' : 'rgba(2,132,199,0.1)',
                  color: mode === 'dark' ? '#4facfe' : '#0284c7'
                }}
              />
            </Box>
            
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={purchasesChartData.map(d => ({ ...d, name: getDayName(d.dayName) }))}>
                <defs>
                  <linearGradient id="colorPurchases" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#4facfe" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#4facfe" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorReturns" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f5576c" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#f5576c" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={mode === 'dark' ? '#334155' : '#e2e8f0'} />
                <XAxis 
                  dataKey="name" 
                  stroke={mode === 'dark' ? '#64748b' : '#94a3b8'}
                  fontSize={12}
                  reversed={isRTL}
                />
                <YAxis 
                  stroke={mode === 'dark' ? '#64748b' : '#94a3b8'}
                  fontSize={12}
                  orientation={isRTL ? 'right' : 'left'}
                />
                <RechartsTooltip 
                  contentStyle={{ 
                    backgroundColor: mode === 'dark' ? '#1e293b' : '#ffffff',
                    border: 'none',
                    borderRadius: 8,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
                  }}
                />
                <Legend />
                <Area 
                  type="monotone" 
                  dataKey="purchasesTotal" 
                  name={t.purchases}
                  stroke="#4facfe" 
                  strokeWidth={2}
                  fillOpacity={1} 
                  fill="url(#colorPurchases)" 
                />
                <Area 
                  type="monotone" 
                  dataKey="returnsTotal" 
                  name={t.returns}
                  stroke="#f5576c" 
                  strokeWidth={2}
                  fillOpacity={1} 
                  fill="url(#colorReturns)" 
                />
              </AreaChart>
            </ResponsiveContainer>
          </Paper>
        )}

        {/* رسم بياني للمبيعات - فقط من لديه DAILY_SALES_VIEW */}
        {hasPermission(['DAILY_SALES_VIEW']) && (
          <Paper 
            elevation={0}
            sx={{ 
              p: 3,
              borderRadius: 3,
              bgcolor: mode === 'dark' ? '#1e293b' : '#ffffff',
              border: `1px solid ${mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box>
                <Typography variant="h6" fontWeight="bold" sx={{ color: mode === 'dark' ? '#f1f5f9' : '#0f172a' }}>
                  {t.salesOverview}
                </Typography>
                <Typography variant="body2" component="div" sx={{ color: mode === 'dark' ? '#94a3b8' : '#64748b' }}>
                  {t.last7Days}
                </Typography>
              </Box>
              <Chip 
                label={t.thisWeek}
                size="small"
                sx={{ 
                  bgcolor: mode === 'dark' ? 'rgba(17,153,142,0.15)' : 'rgba(17,153,142,0.1)',
                  color: mode === 'dark' ? '#38ef7d' : '#11998e'
                }}
              />
            </Box>
            
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={salesChartData.map(d => ({ ...d, name: getDayName(d.dayName) }))}>
                <defs>
                  <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#11998e" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#11998e" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={mode === 'dark' ? '#334155' : '#e2e8f0'} />
                <XAxis 
                  dataKey="name" 
                  stroke={mode === 'dark' ? '#64748b' : '#94a3b8'}
                  fontSize={12}
                  reversed={isRTL}
                />
                <YAxis 
                  stroke={mode === 'dark' ? '#64748b' : '#94a3b8'}
                  fontSize={12}
                  orientation={isRTL ? 'right' : 'left'}
                />
                <RechartsTooltip 
                  contentStyle={{ 
                    backgroundColor: mode === 'dark' ? '#1e293b' : '#ffffff',
                    border: 'none',
                    borderRadius: 8,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
                  }}
                />
                <Area 
                  type="monotone" 
                  dataKey="salesTotal" 
                  name={t.sales}
                  stroke="#11998e" 
                  strokeWidth={2}
                  fillOpacity={1} 
                  fill="url(#colorSales)" 
                />
              </AreaChart>
            </ResponsiveContainer>
          </Paper>
        )}
      </Box>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* 📊 BRANCH COMPARISON + ALERTS                                 */}
      {/* مقارنة الفروع: فقط DAILY_SALES_VIEW                          */}
      {/* أمين المخزن لا يرى مقارنة الفروع ولا المبيعات                */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' },
        gap: 3,
        mb: 3
      }}>
        {/* مقارنة مبيعات الفروع - فقط DAILY_SALES_VIEW */}
        {hasPermission(['DAILY_SALES_VIEW']) && branchSales.length > 0 && (
          <Paper 
            elevation={0}
            sx={{ 
              p: 3,
              borderRadius: 3,
              bgcolor: mode === 'dark' ? '#1e293b' : '#ffffff',
              border: `1px solid ${mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box>
                <Typography variant="h6" fontWeight="bold" sx={{ color: mode === 'dark' ? '#f1f5f9' : '#0f172a' }}>
                  {t.branchComparison}
                </Typography>
                <Typography variant="body2" component="div" sx={{ color: mode === 'dark' ? '#94a3b8' : '#64748b' }}>
                  {t.last30Days}
                </Typography>
              </Box>
              <Chip 
                label={t.thisMonth}
                size="small"
                sx={{ 
                  bgcolor: mode === 'dark' ? 'rgba(102,126,234,0.15)' : 'rgba(102,126,234,0.1)',
                  color: mode === 'dark' ? '#667eea' : '#5a67d8'
                }}
              />
            </Box>
            
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={branchSales.map(b => ({
                name: language === 'ar' ? b.branchNameAr : b.branchName,
                [t.totalSalesAmount]: b.totalSales,
              }))}>
                <CartesianGrid strokeDasharray="3 3" stroke={mode === 'dark' ? '#334155' : '#e2e8f0'} />
                <XAxis 
                  dataKey="name" 
                  stroke={mode === 'dark' ? '#64748b' : '#94a3b8'} 
                  fontSize={11} 
                />
                <YAxis 
                  stroke={mode === 'dark' ? '#64748b' : '#94a3b8'} 
                  fontSize={12} 
                  orientation={isRTL ? 'right' : 'left'} 
                />
                <RechartsTooltip 
                  contentStyle={{ 
                    backgroundColor: mode === 'dark' ? '#1e293b' : '#ffffff',
                    border: 'none',
                    borderRadius: 8,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
                  }}
                />
                <Bar dataKey={t.totalSalesAmount} radius={[6, 6, 0, 0]}>
                  {branchSales.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={BRANCH_COLORS[index % BRANCH_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        )}

        {/* التنبيهات - الباكند يرسل فقط التنبيهات المتعلقة بدور المستخدم */}
        <Paper 
          elevation={0}
          sx={{ 
            p: 3,
            borderRadius: 3,
            bgcolor: mode === 'dark' ? '#1e293b' : '#ffffff',
            border: `1px solid ${mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <NotificationsIcon sx={{ color: mode === 'dark' ? '#4facfe' : '#0284c7' }} />
            <Typography variant="h6" fontWeight="bold" sx={{ color: mode === 'dark' ? '#f1f5f9' : '#0f172a' }}>
              {t.alerts}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {alerts.length > 0 ? (
              alerts.map((alert, index) => (
                <Alert 
                  key={`alert-${index}`}
                  severity={
                    alert.type === 'warning' ? 'warning' : 
                    alert.type === 'info' ? 'info' : 
                    alert.type === 'error' ? 'error' : 'success'
                  }
                  sx={{ 
                    borderRadius: 2,
                    bgcolor: alert.type === 'warning' 
                      ? (mode === 'dark' ? 'rgba(251,191,36,0.1)' : 'rgba(251,191,36,0.1)')
                      : alert.type === 'error'
                        ? (mode === 'dark' ? 'rgba(239,68,68,0.1)' : 'rgba(239,68,68,0.1)')
                        : alert.type === 'info'
                          ? (mode === 'dark' ? 'rgba(79,172,254,0.1)' : 'rgba(79,172,254,0.1)')
                          : (mode === 'dark' ? 'rgba(34,197,94,0.1)' : 'rgba(34,197,94,0.1)'),
                    '& .MuiAlert-icon': { 
                      color: alert.type === 'warning' ? '#f59e0b' 
                        : alert.type === 'error' ? '#ef4444'
                        : alert.type === 'info' ? '#4facfe' 
                        : '#22c55e'
                    }
                  }}
                >
                  <AlertTitle sx={{ fontWeight: 600 }}>
                    {language === 'ar' ? (alert.title || 'تنبيه') : (alert.titleEn || 'Alert')}
                  </AlertTitle>
                  <Typography variant="body2" sx={{ mb: alert.items && alert.items.length > 0 ? 1 : 0 }}>
                    {language === 'ar' ? (alert.message || '') : (alert.messageEn || '')}
                  </Typography>
                  {alert.items && alert.items.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      {alert.items.slice(0, 5).map((item, idx) => (
                        <Chip
                          key={idx}
                          label={`${language === 'ar' ? item.itemNameAr : item.itemName} (${item.currentStock}/${item.minStockLevel})`}
                          size="small"
                          sx={{ 
                            mr: 0.5, 
                            mb: 0.5, 
                            bgcolor: 'rgba(251,191,36,0.2)',
                            fontSize: '0.7rem'
                          }}
                        />
                      ))}
                      {alert.items.length > 5 && (
                        <Chip
                          label={`+${alert.items.length - 5} ${language === 'ar' ? 'أصناف أخرى' : 'more items'}`}
                          size="small"
                          sx={{ mr: 0.5, mb: 0.5, bgcolor: 'rgba(251,191,36,0.15)', fontSize: '0.7rem' }}
                        />
                      )}
                    </Box>
                  )}
                </Alert>
              ))
            ) : (
              <Alert 
                severity="success" 
                sx={{ 
                  borderRadius: 2,
                  bgcolor: mode === 'dark' ? 'rgba(34,197,94,0.1)' : 'rgba(34,197,94,0.1)',
                  '& .MuiAlert-icon': { color: '#22c55e' }
                }}
              >
                <AlertTitle sx={{ fontWeight: 600 }}>{t.systemHealth}</AlertTitle>
                {t.healthy} ✓
              </Alert>
            )}
          </Box>
        </Paper>
      </Box>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* ⚡ QUICK ACTIONS + RECENT ACTIVITY + DISTRIBUTION             */}
      {/* الإجراءات السريعة: كل زر مقيد بصلاحيته                       */}
      {/* النشاط الأخير: الباكند يفلتر حسب دور المستخدم                */}
      {/* توزيع المخزون: فقط WAREHOUSE_VIEW أو STOCK_LEVEL_VIEW        */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
        gap: 3
      }}>
        {/* الإجراءات السريعة */}
        <Paper 
          elevation={0}
          sx={{ 
            p: 3,
            borderRadius: 3,
            bgcolor: mode === 'dark' ? '#1e293b' : '#ffffff',
            border: `1px solid ${mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <SpeedIcon sx={{ color: mode === 'dark' ? '#4facfe' : '#0284c7' }} />
            <Typography variant="h6" fontWeight="bold" sx={{ color: mode === 'dark' ? '#f1f5f9' : '#0f172a' }}>
              {t.quickActions}
            </Typography>
          </Box>
          
          <List disablePadding>
            <QuickActionButton 
              title={t.newPurchase}
              icon={<ShoppingCartIcon />}
              onClick={() => navigate('/purchase-invoices')}
              permission={['PURCHASE_INVOICE_CREATE']}
            />
            <QuickActionButton 
              title={t.addItem}
              icon={<AddIcon />}
              onClick={() => navigate('/items')}
              permission={['ITEMS_CREATE']}
            />
            <QuickActionButton 
              title={t.stockCount}
              icon={<StockCountIcon />}
              onClick={() => navigate('/stock-counts')}
              permission={['STOCK_COUNT_CREATE']}
            />
            <QuickActionButton 
              title={t.viewReports}
              icon={<ReceiptIcon />}
              onClick={() => navigate('/reports/stock-levels')}
              permission={['REPORT_STOCK_LEVEL']}
            />
          </List>
        </Paper>

        {/* النشاط الأخير - الباكند يفلتر: مدير الفرع يرى نشاط فرعه، أمين المخزن يرى نشاط مخازنه */}
        <Paper 
          elevation={0}
          sx={{ 
            p: 3,
            borderRadius: 3,
            bgcolor: mode === 'dark' ? '#1e293b' : '#ffffff',
            border: `1px solid ${mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <AccessTimeIcon sx={{ color: mode === 'dark' ? '#4facfe' : '#0284c7' }} />
            <Typography variant="h6" fontWeight="bold" sx={{ color: mode === 'dark' ? '#f1f5f9' : '#0f172a' }}>
              {t.recentActivity}
            </Typography>
          </Box>

          {recentActivities.length > 0 ? (
            <List disablePadding sx={{ maxHeight: 320, overflow: 'auto' }}>
              {recentActivities.map((activity, index) => (
                <ListItemButton key={index} sx={{ borderRadius: 2, mb: 0.5, py: 1 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Box sx={{ 
                      width: 28, 
                      height: 28, 
                      borderRadius: '50%', 
                      display: 'flex',
                      alignItems: 'center', 
                      justifyContent: 'center',
                      bgcolor: `${getActivityColor(activity.type)}20`, 
                      color: getActivityColor(activity.type) 
                    }}>
                      {getActivityIcon(activity.icon)}
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={language === 'ar' ? activity.description : activity.descriptionEn}
                    secondary={
                      activity.amount 
                        ? formatCurrency(activity.amount) 
                        : new Date(activity.date).toLocaleDateString(language === 'ar' ? 'ar-KW' : 'en-GB')
                    }
                    primaryTypographyProps={{ 
                      fontSize: '0.8rem', 
                      fontWeight: 500, 
                      noWrap: true,
                      color: mode === 'dark' ? '#e2e8f0' : '#334155' 
                    }}
                    secondaryTypographyProps={{ 
                      fontSize: '0.7rem',
                      color: activity.amount ? '#11998e' : (mode === 'dark' ? '#64748b' : '#94a3b8') 
                    }}
                  />
                </ListItemButton>
              ))}
            </List>
          ) : (
            <Typography 
              variant="body2" 
              sx={{ 
                color: mode === 'dark' ? '#64748b' : '#94a3b8', 
                textAlign: 'center', 
                py: 4 
              }}
            >
              {t.noActivity}
            </Typography>
          )}
        </Paper>

        {/* توزيع المخزون - فقط من لديه صلاحية عرض المخازن أو المخزون */}
        {hasPermission(['WAREHOUSE_VIEW', 'STOCK_LEVEL_VIEW']) && (
          <Paper 
            elevation={0}
            sx={{ 
              p: 3,
              borderRadius: 3,
              bgcolor: mode === 'dark' ? '#1e293b' : '#ffffff',
              border: `1px solid ${mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
            }}
          >
            <Typography variant="h6" fontWeight="bold" sx={{ color: mode === 'dark' ? '#f1f5f9' : '#0f172a', mb: 2 }}>
              {t.inventoryDistribution}
            </Typography>
            
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={inventoryData.map(d => ({ 
                    name: language === 'ar' ? d.categoryNameAr : d.categoryNameEn, 
                    value: d.itemCount 
                  }))}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={3}
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}
                  labelLine={true}
                >
                  {inventoryData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <RechartsTooltip 
                  contentStyle={{ 
                    backgroundColor: mode === 'dark' ? '#1e293b' : '#ffffff',
                    border: 'none',
                    borderRadius: 8,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default DashboardPage;